/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import {
  useImperativeHandle,
  useRef,
  useState,
  type Dispatch,
  type Ref,
  type SetStateAction,
} from 'react';
import type { BottomSheetRefs } from '@airasia-phoenix/react-native/components/BottomSheetView';
import { useIsMobileWeb } from './useScreenSize';

export interface MobileWebSheet {
  /** Render `FlightsBottomSheetView` when `true`, the Phoenix sheet otherwise. */
  isMobileWeb: boolean;
  /** Pass as `visible` to `FlightsBottomSheetView`. */
  isOpen: boolean;
  /** Call with `false` in `FlightsBottomSheetView`'s `onDismiss`. */
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

/**
 * Lets a Phoenix-ref-driven sheet use `FlightsBottomSheetView` on mobile web
 * without changing its callers.
 *
 * On mobile web the given ref points at a small handle whose
 * `toggleBottomSheetView()` flips `isOpen`, so existing
 * `ref.current?.toggleBottomSheetView()` calls keep working. Everywhere else
 * the ref is left to the Phoenix sheet.
 *
 * `isMobileWeb` only follows the screen width while the sheet is closed, so a
 * resize across `md` can't swap the sheet out from under the user.
 */
export const useMobileWebSheet = (
  ref: Ref<BottomSheetRefs> | undefined,
): MobileWebSheet => {
  const isMobileWebNow = useIsMobileWeb();
  const [isOpen, setIsOpen] = useState(false);
  const lockedIsMobileWeb = useRef(isMobileWebNow);
  if (!isOpen) lockedIsMobileWeb.current = isMobileWebNow;
  const isMobileWeb = lockedIsMobileWeb.current;

  useImperativeHandle(
    isMobileWeb ? ref : undefined,
    () => ({
      toggleBottomSheetView: () => setIsOpen((open) => !open),
      swipeToIndex: () => {},
      isVisible: isOpen,
    }),
    [isOpen],
  );

  return { isMobileWeb, isOpen, setIsOpen };
};
